import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = 'https://hoosierlifeinsurance.com';

export default function NoMedicalExamLifeInsuranceIndianaGuide() {
  useEffect(() => {
    document.title = 'No Medical Exam Life Insurance in Indiana — Complete Guide 2026';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'No medical exam life insurance in Indiana: how it works, who qualifies, best carriers, and how to get approved fast. Coverage up to $3M without a doctor visit.');

    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', 'no medical exam life insurance Indiana, no exam life insurance Indianapolis, instant approval life insurance Indiana');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `${SITE_URL}/resources/no-medical-exam-life-insurance-indiana-guide`;

    const schemas = [
      {
        '@context': 'https://schema.org', '@type': 'Article',
        headline: 'No Medical Exam Life Insurance in Indiana: Complete Guide',
        description: 'How no-exam life insurance works in Indiana, who qualifies, best carriers, and how to get approved in 24 hours without a doctor visit.',
        author: { '@type': 'Person', name: 'Benjamin Wright' },
        publisher: { '@type': 'Organization', name: 'Hoosier Life Insurance', url: SITE_URL },
        datePublished: '2025-01-14T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: `${SITE_URL}/resources/no-medical-exam-life-insurance-indiana-guide`,
      },
      {
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Resources', item: `${SITE_URL}/resources` },
          { '@type': 'ListItem', position: 3, name: 'No Medical Exam Life Insurance Indiana Guide', item: `${SITE_URL}/resources/no-medical-exam-life-insurance-indiana-guide` },
        ],
      },
      {
        '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: [
          { '@type': 'Question', name: 'How much no-exam life insurance can I get in Indiana?', acceptedAnswer: { '@type': 'Answer', text: 'Accelerated underwriting (no-exam) policies in Indiana can provide up to $3 million in coverage for healthy applicants under age 60. Simplified issue policies typically max out at $500K-$1M. Guaranteed issue policies are limited to $25K-$50K. The amount you qualify for depends on your age, health history, and the carrier.' } },
          { '@type': 'Question', name: 'Is no-exam life insurance more expensive than traditional life insurance?', acceptedAnswer: { '@type': 'Answer', text: 'For healthy applicants, accelerated underwriting (no-exam) policies are priced nearly identically to fully underwritten policies. You\'re not paying a premium for skipping the exam. Simplified issue policies cost 10-20% more. Guaranteed issue policies cost significantly more due to the guaranteed acceptance.' } },
          { '@type': 'Question', name: 'How fast can I get approved for no-exam life insurance in Indiana?', acceptedAnswer: { '@type': 'Answer', text: 'Accelerated underwriting decisions are typically made in 24-72 hours. Some carriers offer same-day approval. Simplified issue policies are often approved in 24-48 hours. Guaranteed issue policies are approved immediately — no underwriting required.' } },
        ],
      },
    ];

    schemas.forEach((schema, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-noexam-${i}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
    return () => { schemas.forEach((_, i) => document.getElementById(`schema-noexam-${i}`)?.remove()); };
  }, []);

  const policyTypes = [
    {
      name: 'Accelerated Underwriting',
      badge: 'Best Option',
      badgeColor: 'bg-green-600',
      maxCoverage: 'Up to $3 million',
      approval: '24-72 hours',
      healthQuestions: 'Yes (detailed)',
      medExam: 'No exam required',
      cost: 'Same as traditional',
      bestFor: 'Healthy applicants under 60 who want maximum coverage without the hassle of a medical exam',
      howItWorks: 'Carriers use your prescription history, MIB records, and driving record to underwrite your policy algorithmically — no nurse visit required.',
    },
    {
      name: 'Simplified Issue',
      badge: 'Good Option',
      badgeColor: 'bg-[#D97642]',
      maxCoverage: 'Up to $500K-$1M',
      approval: '24-48 hours',
      healthQuestions: 'Yes (simplified)',
      medExam: 'No exam required',
      cost: '10-20% more than traditional',
      bestFor: 'Applicants with minor health conditions who want fast approval without a full medical exam',
      howItWorks: 'You answer 5-15 health questions. No blood draw, no urine sample, no nurse visit. Approval is based on your answers and database checks.',
    },
    {
      name: 'Guaranteed Issue',
      badge: 'Last Resort',
      badgeColor: 'bg-gray-600',
      maxCoverage: 'Up to $25K-$50K',
      approval: 'Immediate',
      healthQuestions: 'None',
      medExam: 'No exam required',
      cost: '40-60% more than traditional',
      bestFor: 'Seniors with serious health conditions who cannot qualify for any other policy type',
      howItWorks: 'No health questions, no exam, no medical records. Everyone is accepted. Typically has a 2-year waiting period before full death benefit pays.',
    },
  ];

  return (
    <>
      <title>No Medical Exam Life Insurance in Indiana — 2026 Guide | Hoosier Life</title>
      <article className="min-h-screen bg-white">
        <section className="relative bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white py-20">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative max-w-4xl mx-auto px-6">
            <div className="mb-6">
              <Link to="/resources" className="inline-flex items-center text-white/70 hover:text-white transition-colors text-sm">
                <i className="ri-arrow-left-line mr-2" />Back to Resources
              </Link>
            </div>
            <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#D97642] text-sm font-semibold px-4 py-2 rounded-full mb-6">
              <i className="ri-stethoscope-line" />No Exam Coverage
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">No Medical Exam Life Insurance in Indiana — Get Covered in 24–72 Hours</h1>
            <p className="text-xl text-white/80 mb-8">Get covered in 24-72 hours without a doctor visit, blood draw, or urine sample. Here's everything Indiana residents need to know.</p>
            <div className="flex flex-wrap gap-4 text-sm text-white/70">
              <span><i className="ri-calendar-line mr-2" />Updated January 2026</span>
              <span><i className="ri-time-line mr-2" />8 min read</span>
              <span><i className="ri-map-pin-line mr-2" />Indiana No-Exam Guide</span>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-6 py-16">
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              The traditional life insurance process — scheduling a nurse visit, getting blood drawn, waiting 4-8 weeks for approval — is becoming obsolete. Today, most Indiana residents can get <strong>up to $3 million in coverage without ever seeing a doctor</strong>.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Here's how no-exam life insurance works, who qualifies, and which Indiana carriers offer the best rates and fastest approvals.
            </p>
          </div>

          <div className="bg-[#FDF6F0] border-l-4 border-[#D97642] p-6 mb-12 rounded-r-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
              <i className="ri-lightbulb-line text-[#D97642] mr-3 text-2xl" />Key Facts About No-Exam Life Insurance in Indiana
            </h3>
            <ul className="space-y-2 text-gray-800">
              {[
                'Healthy applicants under 60 can get up to $3M without a medical exam',
                'Approval in 24-72 hours (vs. 4-8 weeks for traditional policies)',
                'Rates are nearly identical to traditional policies for healthy applicants',
                'Carriers use prescription history, MIB records, and driving records instead of exams',
                'Available for term life, whole life, and final expense policies',
              ].map(f => (
                <li key={f} className="flex items-start"><i className="ri-check-line text-[#D97642] mr-3 mt-1 text-xl" /><span>{f}</span></li>
              ))}
            </ul>
          </div>

          {/* Policy Types */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">3 Types of No-Exam Life Insurance in Indiana</h2>
            <div className="space-y-6">
              {policyTypes.map(type => (
                <div key={type.name} className="bg-white border-2 border-gray-200 rounded-2xl p-8">
                  <div className="flex items-start justify-between gap-4 mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">{type.name}</h3>
                    <span className={`${type.badgeColor} text-white text-sm font-bold px-4 py-2 rounded-full whitespace-nowrap`}>{type.badge}</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    {[
                      { label: 'Max Coverage', value: type.maxCoverage },
                      { label: 'Approval Time', value: type.approval },
                      { label: 'Cost vs. Traditional', value: type.cost },
                    ].map(stat => (
                      <div key={stat.label} className="bg-gray-50 rounded-xl p-4 text-center">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">{stat.label}</p>
                        <p className="font-bold text-gray-900">{stat.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Best For</p>
                      <p className="text-gray-700 text-sm">{type.bestFor}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">How It Works</p>
                      <p className="text-gray-700 text-sm">{type.howItWorks}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Who Qualifies */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Who Qualifies for No-Exam Life Insurance in Indiana?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-green-600 text-2xl" />Likely to Qualify
                </h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  {[
                    'Ages 18-60 in good health',
                    'No major health conditions in past 5 years',
                    'Normal BMI (18.5-30)',
                    'Non-smoker or quit 12+ months ago',
                    'No recent DUI or reckless driving',
                    'No felony convictions',
                    'Controlled high blood pressure (on medication)',
                    'Well-managed type 2 diabetes',
                  ].map(i => <li key={i} className="flex items-start gap-2"><i className="ri-check-line text-green-600 mt-1 flex-shrink-0" />{i}</li>)}
                </ul>
              </div>
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-close-circle-line text-red-600 text-2xl" />May Need Traditional Underwriting
                </h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  {[
                    'Ages 61+ (some carriers still offer no-exam)',
                    'Cancer diagnosis in past 5 years',
                    'Heart attack or stroke in past 2 years',
                    'Uncontrolled diabetes (A1C above 8)',
                    'Severe obesity (BMI above 40)',
                    'Requesting coverage over $3 million',
                    'Multiple serious health conditions',
                    'Recent hospitalization for serious illness',
                  ].map(i => <li key={i} className="flex items-start gap-2"><i className="ri-close-line text-red-500 mt-1 flex-shrink-0" />{i}</li>)}
                </ul>
              </div>
            </div>
          </div>

          {/* Best Carriers */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Best No-Exam Life Insurance Carriers for Indiana Residents</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-6 py-4 text-left font-bold">Carrier</th>
                    <th className="px-6 py-4 text-left font-bold">Max No-Exam Coverage</th>
                    <th className="px-6 py-4 text-left font-bold">Max Age</th>
                    <th className="px-6 py-4 text-left font-bold">Approval Time</th>
                    <th className="px-6 py-4 text-left font-bold">Standout Feature</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[
                    { carrier: 'Banner Life', max: '$3 million', age: 60, time: '24-48 hrs', feature: 'Highest no-exam limit in Indiana' },
                    { carrier: 'Pacific Life', max: '$3 million', age: 60, time: '24-72 hrs', feature: 'Best for applicants with minor health issues' },
                    { carrier: 'Protective Life', max: '$2 million', age: 60, time: '24-48 hrs', feature: 'Fastest approval process' },
                    { carrier: 'Prudential', max: '$1 million', age: 65, time: '24-72 hrs', feature: 'Best for ages 60-65' },
                    { carrier: 'Mutual of Omaha', max: '$500K', age: 70, time: '24-48 hrs', feature: 'Best simplified issue for seniors' },
                  ].map(row => (
                    <tr key={row.carrier} className="hover:bg-[#F9F7F4]">
                      <td className="px-6 py-4 font-bold text-gray-900">{row.carrier}</td>
                      <td className="px-6 py-4 text-[#D97642] font-bold">{row.max}</td>
                      <td className="px-6 py-4 text-gray-700">Up to {row.age}</td>
                      <td className="px-6 py-4 text-green-600 font-semibold">{row.time}</td>
                      <td className="px-6 py-4 text-gray-700 text-sm">{row.feature}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* FAQ */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Common Questions</h2>
            <div className="space-y-4">
              {[
                { q: 'How much no-exam life insurance can I get in Indiana?', a: 'Accelerated underwriting policies can provide up to $3 million for healthy applicants under 60. Simplified issue maxes out at $500K-$1M. Guaranteed issue is limited to $25K-$50K.' },
                { q: 'Is no-exam life insurance more expensive?', a: 'For healthy applicants, accelerated underwriting policies are priced nearly identically to fully underwritten policies. Simplified issue costs 10-20% more. Guaranteed issue costs 40-60% more.' },
                { q: 'How fast can I get approved?', a: 'Accelerated underwriting: 24-72 hours. Simplified issue: 24-48 hours. Guaranteed issue: immediate. Some carriers offer same-day approval for accelerated underwriting.' },
              ].map(faq => (
                <div key={faq.q} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-question-line text-[#D97642] mr-3 text-2xl" />{faq.q}
                  </h3>
                  <p className="text-gray-700 text-sm pl-11">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-2xl p-8 mb-12">
            <h2 className="text-3xl font-bold mb-4">Get Approved in 24 Hours — No Exam Required</h2>
            <p className="text-xl text-white/80 mb-8">Benjamin Wright can get you covered today without a doctor visit. Compare no-exam rates from 20+ Indiana carriers.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/no-exam-life-insurance-indianapolis" className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-50 transition-colors whitespace-nowrap">See No-Exam Rates</Link>
              <Link to="/book-appointment" className="bg-[#0F2A3F] text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-[#0A1F2E] transition-colors border-2 border-white/30 whitespace-nowrap">Talk to an Agent</Link>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { to: '/no-exam-life-insurance-indianapolis', icon: 'ri-stethoscope-line', title: 'No-Exam Life Insurance Indianapolis', desc: 'Compare no-exam rates from top Indianapolis carriers' },
                { to: '/no-medical-exam', icon: 'ri-file-list-3-line', title: 'No Medical Exam Life Insurance', desc: 'Our full no-exam life insurance product page' },
                { to: '/resources/life-insurance-mistakes-indiana-families', icon: 'ri-error-warning-line', title: '7 Mistakes Indiana Families Make', desc: 'Avoid the most costly life insurance errors' },
              ].map(r => (
                <Link key={r.to} to={r.to} className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                  <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className={`${r.icon} text-2xl`} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">{r.title}</h3>
                  <p className="text-sm text-gray-600">{r.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Questions About No-Exam Life Insurance?</h2>
            <p className="text-xl text-gray-700 mb-8">Our Indiana-based agents can get you covered today without a doctor visit. Let's find the right no-exam policy for your needs.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:3176482983" className="bg-[#1A3A52] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#0F2A3F] transition-colors whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-phone-line mr-2" />Call (317) 648-2983
              </a>
              <Link to="/contact" className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors border-2 border-[#1A3A52] whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-message-3-line mr-2" />Send a Message
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}
